/**
 * src/components/OnboardingCompleteRoute.tsx — gate the customer app behind
 * a completed onboarding (address + card + first crate ordered).
 *
 * Nested INSIDE <CustomerRoleRoute> (so role routing already ran) but OUTSIDE
 * the /welcome route (so the flow itself stays reachable). An incomplete
 * customer is redirected to /welcome; a complete one passes through.
 *
 * Resumable + non-trapping by design: /profile (sign-out, account) stays
 * reachable because it lives above this gate in the route tree, and the
 * welcome flow carries its own sign-out. We never lock the user out of
 * leaving — we only gate the main app until setup is done.
 *
 * Perf: once complete in a session we cache a per-email flag and skip the
 * fetch entirely on subsequent dashboard loads (completion is monotonic —
 * you can't un-order your first crate). The flag is email-keyed so a second
 * user signing in on the same tab isn't waved through on the first user's
 * completion.
 */

import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { LoadingFallback } from './LoadingFallback';

const DONE_KEY = 'attic_onboarding_done';

function isDoneFor(email: string): boolean {
  try { return sessionStorage.getItem(DONE_KEY) === email; } catch { return false; }
}

/**
 * Mark onboarding complete for this email in the session cache. Called by the
 * welcome flow on the final step so navigating to /dashboard doesn't briefly
 * bounce back to /welcome while the gate's own fetch catches up.
 */
export function markOnboardingDone(email: string): void {
  try { sessionStorage.setItem(DONE_KEY, email); } catch { /* noop */ }
}

export function OnboardingCompleteRoute() {
  const { customerEmail } = useAuth();

  // Fast path: skip the fetch + spinner if we've already seen this exact
  // customer complete onboarding earlier this session.
  if (customerEmail && isDoneFor(customerEmail)) {
    return <Outlet />;
  }
  return <OnboardingGate email={customerEmail} />;
}

function OnboardingGate({ email }: { email: string | null }) {
  const status = useOnboardingStatus();

  useEffect(() => {
    if (status.complete && email) markOnboardingDone(email);
  }, [status.complete, email]);

  if (status.loading) return <LoadingFallback />;
  return status.complete ? <Outlet /> : <Navigate to="/welcome" replace />;
}
